import { useEffect, useState } from "react";
import { getAssetKind, getAssetPreviewUrl, isSvgAsset } from "./galleryUtils";

const NORMALIZED_TARGETS = [
  { width: 1920, height: 1080, ratio: 1920 / 1080 },
  { width: 1920, height: 1920, ratio: 1 },
  { width: 1080, height: 1920, ratio: 1080 / 1920 },
];

const normalizedAssetCache = new Map();
const normalizedAssetPromiseCache = new Map();

function shouldNormalizeUrl(url) {
  if (!url || typeof window === "undefined") {
    return false;
  }

  if (url.startsWith("blob:") || url.startsWith("data:")) {
    return true;
  }

  try {
    const resolvedUrl = new URL(url, window.location.origin);
    return resolvedUrl.origin === window.location.origin;
  } catch (error) {
    return false;
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    image.src = url;
  });
}

function canvasToObjectUrl(canvas, mimeType) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to normalize image"));
          return;
        }

        resolve(URL.createObjectURL(blob));
      },
      mimeType,
      mimeType === "image/png" ? undefined : 0.9
    );
  });
}

async function normalizeImageUrl(url) {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${url}`);
  }

  const blob = await response.blob();
  const imageUrl = URL.createObjectURL(blob);

  try {
    const image = await loadImage(imageUrl);
    const sourceRatio = image.naturalWidth / image.naturalHeight;
    const nearestTarget = NORMALIZED_TARGETS.reduce((closest, target) => {
      const closestDelta = Math.abs(closest.ratio - sourceRatio);
      const targetDelta = Math.abs(target.ratio - sourceRatio);
      return targetDelta < closestDelta ? target : closest;
    }, NORMALIZED_TARGETS[0]);

    const canvas = document.createElement("canvas");
    canvas.width = nearestTarget.width;
    canvas.height = nearestTarget.height;
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Canvas context unavailable");
    }

    const targetRatio = nearestTarget.width / nearestTarget.height;
    let cropWidth = image.naturalWidth;
    let cropHeight = image.naturalHeight;
    let cropX = 0;
    let cropY = 0;

    if (sourceRatio > targetRatio) {
      cropWidth = image.naturalHeight * targetRatio;
      cropX = (image.naturalWidth - cropWidth) / 2;
    } else if (sourceRatio < targetRatio) {
      cropHeight = image.naturalWidth / targetRatio;
      cropY = (image.naturalHeight - cropHeight) / 2;
    }

    context.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      nearestTarget.width,
      nearestTarget.height
    );

    return canvasToObjectUrl(
      canvas,
      blob.type === "image/png" || blob.type === "image/webp" ? "image/png" : "image/jpeg"
    );
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

async function getNormalizedAssetUrl(url) {
  if (!shouldNormalizeUrl(url)) {
    return url;
  }

  if (normalizedAssetCache.has(url)) {
    return normalizedAssetCache.get(url);
  }

  if (!normalizedAssetPromiseCache.has(url)) {
    normalizedAssetPromiseCache.set(
      url,
      normalizeImageUrl(url)
        .then((normalizedUrl) => {
          normalizedAssetCache.set(url, normalizedUrl);
          return normalizedUrl;
        })
        .catch(() => url)
    );
  }

  return normalizedAssetPromiseCache.get(url);
}

function useNormalizedGalleryAsset(asset) {
  const kind = getAssetKind(asset);
  const isSvg = isSvgAsset(asset);
  const previewUrl = getAssetPreviewUrl(asset);
  const sourceUrl = kind === "pdf" ? previewUrl : asset?.url || "";
  const [resolvedUrl, setResolvedUrl] = useState(
    kind === "image" && !isSvg
      ? normalizedAssetCache.get(sourceUrl) || sourceUrl
      : sourceUrl
  );

  useEffect(() => {
    let active = true;

    if (!sourceUrl) {
      setResolvedUrl("");
      return undefined;
    }

    if (kind !== "image" || isSvg) {
      setResolvedUrl(sourceUrl);
      return undefined;
    }

    getNormalizedAssetUrl(sourceUrl)
      .then((normalizedUrl) => {
        if (active) {
          setResolvedUrl(normalizedUrl || sourceUrl);
        }
      })
      .catch(() => {
        if (active) {
          setResolvedUrl(sourceUrl);
        }
      });

    return () => {
      active = false;
    };
  }, [isSvg, kind, sourceUrl]);

  return resolvedUrl || sourceUrl;
}

export default useNormalizedGalleryAsset;
